import { OAUTH_ERROR_CODES, type Auth0LoginErrorType } from "../analytics";

/**
 * A failed Auth0 login step, with the code that `Auth0 Login Failed` reports.
 * The message is for the UI only and is never sent to analytics.
 */
export class Auth0LoginFailure extends Error {
  constructor(
    readonly errorType: Auth0LoginErrorType,
    message: string,
    options?: { cause: unknown },
  ) {
    super(message, options);
    this.name = "Auth0LoginFailure";
  }
}

/** The code to report for an error caught at the end of a login flow. */
export const auth0LoginErrorType = (error: unknown): Auth0LoginErrorType =>
  error instanceof Auth0LoginFailure ? error.errorType : "unknown";

/** Maps the OAuth `error` param of a failed prompt to a fixed code. */
export const oauthErrorType = (code: string | undefined): Auth0LoginErrorType =>
  OAUTH_ERROR_CODES.find((known) => known === code) ?? "oauth_other";

/**
 * The failure for a prompt that returned `type: "error"`. `params` is typed as
 * always present but is read defensively: without it, the code is `oauth_other`
 * and the message "Authentication failed".
 */
export const oauthPromptFailure = (result: {
  readonly params?: Readonly<Record<string, string | undefined>> | undefined;
}): Auth0LoginFailure =>
  new Auth0LoginFailure(
    oauthErrorType(result.params?.error),
    result.params?.error_description || "Authentication failed",
  );

/** The UI message of a caught value. */
export const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

/** Throws a `csrf_mismatch` failure unless the returned OAuth `state` carries the expected CSRF token. */
export const assertCsrfState = (state: string, expected: string | null | undefined): void => {
  let returned: unknown;
  try {
    const parsed: unknown = JSON.parse(state);
    returned = typeof parsed === "object" && parsed !== null && "csrfToken" in parsed ? parsed.csrfToken : undefined;
  } catch (error) {
    throw new Auth0LoginFailure("csrf_mismatch", "CSRF token mismatch", { cause: error });
  }
  if (returned !== expected) {
    throw new Auth0LoginFailure("csrf_mismatch", "CSRF token mismatch");
  }
};
